import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import ControlPanel from '../TorsoImageUploadOverlay/ControlPanel';
import { clipPathToCSS, applyClipPathToCanvas } from '../../utils/displayUploadConfig';

/**
 * Shared image-upload overlay with configurable clip-path.
 *
 * Both the torso and display upload features delegate to this component
 * for drag/zoom interaction, clipped preview rendering, and composite
 * canvas generation.
 *
 * Props:
 *  - imageData          Current upload state ({ dataUri, compositeDataUri, position })
 *  - onCompositeGenerated(compositeDataUri, position)  Callback when canvas is re-rendered
 *  - baseImageSrc       The product image URL that forms the composite background
 *  - clipPathPoints     Clip spec: Array<{ x, y }> (polygon) or { type, css, commands } (shape)
 *  - canvasSize         Square canvas dimension (default 480)
 *  - showDevMode        Render debugging overlay
 *  - isProductAddedToBuild  Lock interaction after product is added
 *  - overlayClassName   CSS class on the outer clipped wrapper (for per-section styling)
 *  - clipBackgroundColor Optional fill rendered inside the clip-path before the uploaded image
 */
const ImageUploadOverlay = ({
    imageData = null,
    onCompositeGenerated = () => {},
    baseImageSrc = '',
    clipPathPoints = [],
    canvasSize = 480,
    showDevMode = false,
    isProductAddedToBuild = false,
    overlayClassName = 'torso-image-upload-overlay',
    clipBackgroundColor = null,
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0, scale: 1 });
    const dragStartRef = useRef({ x: 0, y: 0 });
    const onCompositeGeneratedRef = useRef(onCompositeGenerated);
    const containerRef = useRef(null);
    const compositeCanvasRef = useRef(null);
    const compositeTimeoutRef = useRef(null);

    const dataUri = imageData?.dataUri;

    // Keep ref in sync with prop
    onCompositeGeneratedRef.current = onCompositeGenerated;

    // Initialize position from imageData once on mount or when dataUri changes
    useEffect(() => {
        if (imageData?.position) {
            setPosition(imageData.position);
        } else if (dataUri) {
            setPosition({ x: 0, y: 0, scale: 1 });
        }
    }, [dataUri, imageData?.position]);

    const effectivePosition = position;

    // Derive CSS clip-path string once
    const cssClipPath = clipPathToCSS(clipPathPoints);

    // Generate composite image for cart submission — debounced
    useEffect(() => {
        if (!dataUri || !baseImageSrc || !compositeCanvasRef.current) return;

        if (compositeTimeoutRef.current) {
            clearTimeout(compositeTimeoutRef.current);
        }

        compositeTimeoutRef.current = setTimeout(() => {
            const canvas = compositeCanvasRef.current;
            if (!canvas) return;

            const ctx = canvas.getContext('2d');
            canvas.width = canvasSize;
            canvas.height = canvasSize;

            const elementWidth = containerRef.current?.offsetWidth || canvasSize;
            const elementToCanvas = canvasSize / elementWidth;

            const baseImg = new Image();
            baseImg.crossOrigin = 'anonymous';
            baseImg.onload = () => {
                ctx.clearRect(0, 0, canvasSize, canvasSize);
                ctx.drawImage(baseImg, 0, 0, canvasSize, canvasSize);

                const userImg = new Image();
                userImg.onload = () => {
                    ctx.save();

                    // Apply configured clip-path to canvas
                    applyClipPathToCanvas(ctx, clipPathPoints, canvasSize);

                    if (clipBackgroundColor) {
                        ctx.fillStyle = clipBackgroundColor;
                        ctx.fillRect(0, 0, canvasSize, canvasSize);
                    }

                    const cssScaleFactor = Math.min(elementWidth / userImg.width, 1);
                    const baseWidth = userImg.width * cssScaleFactor * elementToCanvas;
                    const baseHeight = userImg.height * cssScaleFactor * elementToCanvas;
                    const finalWidth = baseWidth * position.scale;
                    const finalHeight = baseHeight * position.scale;

                    // Convert screen-pixel offsets to canvas-pixel offsets.
                    const centerX = canvasSize / 2 + position.x * elementToCanvas;
                    const centerY = canvasSize / 2 + position.y * elementToCanvas;

                    ctx.drawImage(
                        userImg,
                        centerX - finalWidth / 2,
                        centerY - finalHeight / 2,
                        finalWidth,
                        finalHeight
                    );

                    ctx.restore();

                    try {
                        const compositeDataUri = canvas.toDataURL('image/png');
                        onCompositeGeneratedRef.current(compositeDataUri, position);
                    } catch (error) {
                        console.error('Failed to generate composite dataUri:', error);
                    }
                };
                userImg.src = dataUri;
            };
            baseImg.src = baseImageSrc;
        }, 300);

        return () => {
            if (compositeTimeoutRef.current) {
                clearTimeout(compositeTimeoutRef.current);
            }
        };
    }, [dataUri, position, baseImageSrc, clipPathPoints, canvasSize, clipBackgroundColor]);

    // ── Drag handlers ───────────────────────────────────────────────────
    const handleMouseDown = useCallback(
        (e) => {
            if (isProductAddedToBuild) return;
            e.preventDefault();
            setIsDragging(true);
            dragStartRef.current = { x: e.clientX - position.x, y: e.clientY - position.y };
        },
        [isProductAddedToBuild, position.x, position.y]
    );

    const handleTouchStart = useCallback(
        (e) => {
            if (isProductAddedToBuild) return;
            e.preventDefault();
            const touch = e.touches[0];
            setIsDragging(true);
            dragStartRef.current = { x: touch.clientX - position.x, y: touch.clientY - position.y };
        },
        [isProductAddedToBuild, position.x, position.y]
    );

    const handleMouseMove = useCallback((e) => {
        e.preventDefault();
        setPosition((prev) => ({
            ...prev,
            x: e.clientX - dragStartRef.current.x,
            y: e.clientY - dragStartRef.current.y,
        }));
    }, []);

    const handleTouchMove = useCallback((e) => {
        e.preventDefault();
        const touch = e.touches[0];
        setPosition((prev) => ({
            ...prev,
            x: touch.clientX - dragStartRef.current.x,
            y: touch.clientY - dragStartRef.current.y,
        }));
    }, []);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.addEventListener('touchmove', handleTouchMove, { passive: false });
            document.addEventListener('touchend', handleMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                document.removeEventListener('touchmove', handleTouchMove);
                document.removeEventListener('touchend', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove]);

    return (
        <>
            {dataUri && (
                <div className={overlayClassName} ref={containerRef} style={{ clipPath: cssClipPath }}>
                    <div
                        className="upload-image-draggable"
                        style={{
                            transform: `translate(calc(-50% + ${effectivePosition.x}px), calc(-50% + ${effectivePosition.y}px)) scale(${effectivePosition.scale})`,
                        }}
                        onMouseDown={handleMouseDown}
                        onTouchStart={handleTouchStart}
                    >
                        <img src={dataUri} alt="Uploaded content" />
                    </div>
                </div>
            )}

            {dataUri && !isProductAddedToBuild && (
                <ControlPanel
                    position={position}
                    onPositionChange={setPosition}
                    onReset={() => setPosition({ x: 0, y: 0, scale: 1 })}
                />
            )}

            {/* Hidden canvas for composite generation */}
            <canvas ref={compositeCanvasRef} style={{ display: 'none' }} />

            {showDevMode && (
                <div className="upload-dev-mode">
                    <div className="clip-boundary" />
                    <p>Developer Mode: Adjust clip-path via config</p>
                    <p>
                        Position: X={effectivePosition.x.toFixed(0)}px, Y={effectivePosition.y.toFixed(0)}px, Scale=
                        {effectivePosition.scale.toFixed(2)}
                    </p>
                    <p>Has dataUri: {dataUri ? 'YES' : 'NO'}</p>
                    <p>Clip points: {JSON.stringify(clipPathPoints)}</p>
                </div>
            )}
        </>
    );
};

ImageUploadOverlay.propTypes = {
    imageData: PropTypes.shape({
        dataUri: PropTypes.string,
        compositeDataUri: PropTypes.string,
        productId: PropTypes.string,
        position: PropTypes.shape({
            x: PropTypes.number,
            y: PropTypes.number,
            scale: PropTypes.number,
        }),
    }),
    onCompositeGenerated: PropTypes.func,
    baseImageSrc: PropTypes.string,
    clipPathPoints: PropTypes.oneOfType([
        PropTypes.arrayOf(
            PropTypes.shape({
                x: PropTypes.number.isRequired,
                y: PropTypes.number.isRequired,
            })
        ),
        PropTypes.shape({
            type: PropTypes.string.isRequired,
            css: PropTypes.string.isRequired,
            commands: PropTypes.array.isRequired,
        }),
    ]),
    canvasSize: PropTypes.number,
    showDevMode: PropTypes.bool,
    isProductAddedToBuild: PropTypes.bool,
    overlayClassName: PropTypes.string,
    clipBackgroundColor: PropTypes.string,
};

export default ImageUploadOverlay;

// Re-export the composite blob helper (unchanged)
export const generateCompositeBlob = (canvasRef) => {
    return new Promise((resolve) => {
        if (!canvasRef.current) {
            resolve(null);
            return;
        }
        canvasRef.current.toBlob(
            (blob) => {
                resolve(blob);
            },
            'image/png',
            1.0
        );
    });
};
